import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface Props {
  /** Writers with a password confirm with it; everyone else gets an emailed code. */
  hasPassword: boolean;
  email: string;
  onClose: () => void;
  onDeleted: () => void;
}

/** Permanently delete the account, confirmed by the password or an emailed 6-digit code. */
const DeleteAccountDialog: React.FC<Props> = ({ hasPassword, email, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const [useCode, setUseCode] = useState(!hasPassword);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messageFrom = (err: any, fallback: string) => {
    const message = err?.response?.data?.message;
    return Array.isArray(message) ? message[0] : message || fallback;
  };

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post('/users/account/deletion-code');
      setCodeSent(true);
    } catch (err: any) {
      setError(
        messageFrom(
          err,
          t('profile.deleteDialog.codeFailed', "We couldn't send the code. Try again."),
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await axios.delete('/users/account', {
        data: useCode ? { code: code.trim() } : { password },
      });
      onDeleted();
    } catch (err: any) {
      setError(
        messageFrom(
          err,
          t('profile.deleteAccountError', 'Failed to delete account. Please check your password.'),
        ),
      );
      setBusy(false);
    }
  };

  const ready = useCode ? /^\d{6}$/.test(code.trim()) : password.length > 0;
  const inputClass =
    'w-full px-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-white dark:bg-dark-bg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-red-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        role="dialog"
        aria-labelledby="delete-account-title"
        onSubmit={confirm}
        className="bg-white dark:bg-dark-surface rounded-2xl p-6 max-w-sm w-full"
      >
        <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full mx-auto mb-4">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
        </div>
        <h3
          id="delete-account-title"
          className="text-lg font-bold text-center text-light-text dark:text-dark-text mb-2"
        >
          {t('profile.deleteDialog.title', 'Delete your account?')}
        </h3>
        <p className="text-sm text-center text-light-text-secondary dark:text-dark-text-secondary mb-4">
          {t(
            'profile.deleteDialog.body',
            'This permanently deletes your stories, characters, notes and AI chats. People you shared a story with lose access to it. Any subscription is cancelled right away, with no refund for the rest of the period. This cannot be undone.',
          )}
        </p>

        {useCode ? (
          codeSent ? (
            <div className="space-y-2 mb-4">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('profile.deleteDialog.codeSent', 'We emailed a code to {{email}}.', { email })}
              </p>
              <label className="block text-sm font-medium text-light-text dark:text-dark-text">
                {t('profile.deleteDialog.codeLabel', 'Code from the email')}
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${inputClass} mt-1 tracking-widest text-center`}
                  autoFocus
                />
              </label>
              <button
                type="button"
                onClick={sendCode}
                disabled={busy}
                className="text-sm underline text-light-text-secondary dark:text-dark-text-secondary"
              >
                {t('profile.deleteDialog.resend', 'Send a new code')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={sendCode}
              disabled={busy}
              className="w-full mb-4 py-3 bg-light-surface dark:bg-dark-border text-light-text dark:text-dark-text font-semibold rounded-xl transition-colors text-sm"
            >
              {t('profile.deleteDialog.sendCode', 'Email me a code to confirm')}
            </button>
          )
        ) : (
          <div className="space-y-2 mb-4">
            <label className="block text-sm font-medium text-light-text dark:text-dark-text">
              {t('profile.deleteDialog.passwordLabel', 'Your password')}
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputClass} mt-1`}
                autoFocus
              />
            </label>
            <button
              type="button"
              onClick={() => {
                setUseCode(true);
                setError(null);
              }}
              className="text-sm underline text-light-text-secondary dark:text-dark-text-secondary"
            >
              {t('profile.deleteDialog.useCode', 'Forgot it? Confirm with an email code instead')}
            </button>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-300 mb-4">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-3 bg-light-surface dark:bg-dark-border text-light-text dark:text-dark-text font-semibold rounded-xl transition-colors text-sm"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !ready}
            className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
          >
            {t('profile.deleteDialog.confirm', 'Delete forever')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeleteAccountDialog;
